import React, { useState, useEffect } from 'react';
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign, FaTimes, FaCheck, FaMapMarkerAlt, FaCalendarAlt, FaWeightHanging, FaRoad } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { transportOrdersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row, Card } from "react-bootstrap";
import Swal from 'sweetalert2';
import { formatNumber } from '../../utils/helpers';

const TransportOrder = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({
    active: 0,
    pending: 0,
    assigned: 0,
    received: 0,
    completed: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);
  
  // Fetch transport orders based on tab with search
  const fetchTransportOrders = async (page = 1, search = searchTerm, tab = activeTab) => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      switch (tab) {
        case "completed":
          response = await transportOrdersAPI.completed(params);
          break;
        case "received":
          response = await transportOrdersAPI.received(params);
          break;
        case "assigned":
          response = await transportOrdersAPI.assigned(params);
          break;
        case "pending":
          response = await transportOrdersAPI.pending(params);
          break;
        default:
          response = await transportOrdersAPI.index(params);
      }

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        
        // Update counts from response
        if (response.data.counts) {
          setCounts({
            all: response.data.counts.all || 0,
            pending: response.data.counts.pending || 0,
            assigned: response.data.counts.assigned || 0,
            received: response.data.counts.received || 0,
            completed: response.data.counts.completed || 0
          });
        }

        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_transport_orders'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transport_orders'));
      console.error('Error fetching transport orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchTransportOrders(1, searchTerm, activeTab);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      const response = await transportOrdersAPI.index({ per_page: 1 });
      if (response.success && response.data.counts) {
        setCounts(response.data.counts);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  useEffect(() => {
    const initialTab = location.state?.activeTab || activeTab;
    const highlightId = location.state?.highlightId;
    const scrollToHighlight = location.state?.scrollToHighlight;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchTransportOrders(1, searchTerm, initialTab);
    fetchAllCounts();

    if (location.state?.activeTab) {
      navigate(location.pathname, { replace: true });
    }
  }, [activeTab]);

  // إضافة useEffect للتمرير إلى الـ booking المميز
  useEffect(() => {
    if (highlightedBookingId && location.state?.scrollToHighlight) {
      const element = document.getElementById(`booking-${highlightedBookingId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 1500);
      }
    }
  }, [data, highlightedBookingId, location.state]);

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    const scrollToHighlight = location.state?.scrollToHighlight;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchTransportOrders(initialPage, searchTerm, initialTab);
    fetchAllCounts();
  }, []);
  

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransportOrders(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await transportOrdersAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('transport_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchTransportOrders(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_transport'));
      }
    } catch (err) {
      setError(t('failed_to_delete_transport'));
      console.error('Error deleting transport:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/transport-orders/details/${id}`, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (order) => {
    if (!order) return 0;
    
    const statusOrder = ['pending', 'assigned', 'received', 'rejected', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(order.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  const getStatusBadge = (status) => {
      const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        approved: { variant: 'success', text: t('approved') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') }
      };
      
      const config = statusConfig[status] || { variant: 'light', text: status };
      return <Badge bg={config.variant}>{config.text}</Badge>;
    };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
    <div className="driver-page">
      <div className="top-bar">
        {/* Search Bar */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_order_driver')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>
        
        {/* Tabs with counts */}
        <div className="tabs">
          <button
            className={activeTab === "active" ? "active" : ""}
            onClick={() => handleTabChange("active")}
          >
            <span className="count-badge">{counts.all}</span>
            {t("all_transports")}
          </button>
          <button
            className={activeTab === "pending" ? "active" : ""}
            onClick={() => handleTabChange("pending")}
          >
            <span className="count-badge">{counts.pending}</span>
            {t("pending_transports")}
          </button>
          <button
            className={activeTab === "assigned" ? "active" : ""}
            onClick={() => handleTabChange("assigned")}
          >
            <span className="count-badge">{counts.assigned}</span>
            {t("assigned_transports")}
          </button>
          <button
            className={activeTab === "received" ? "active" : ""}
            onClick={() => handleTabChange("received")}
          >
            <span className="count-badge">{counts.received}</span>
            {t("received_transports")}
          </button>
          <button
            className={activeTab === "completed" ? "active" : ""}
            onClick={() => handleTabChange("completed")}
          >
            <span className="count-badge">{counts.completed}</span>
            {t("completed_transports")}
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="orders">
        {data.length === 0 ? (
          <div className="text-center py-5">
            <h4>{t('no_transports_found')}</h4>
            {/* <p className="text-muted">{t('no_transports_description')}</p> */}
          </div>
        ) : (
          data.map((order) => (
            <Card 
              className={`mb-3 shadow-sm order-card ${order.id === highlightedBookingId ? "booking-highlight" : ""}`} 
              key={order.id}
              id={`booking-${order.id}`}
            >
              <Card.Body>
                {/* Header Section */}
                <Row className="align-items-center mb-3">
                  <Col md={4}>
                    <div className="d-flex align-items-center">
                      <FaTruck className="text-primary me-2" />
                      <div>
                        <h6 className="mb-0 fw-bold">
                          {t('order_id')}: 
                          {searchTerm ? (
                            <span 
                              dangerouslySetInnerHTML={{
                                __html: order.order_id?.replace(
                                  new RegExp(searchTerm, 'gi'), 
                                  match => `<mark class="bg-warning">${match}</mark>`
                                )
                              }} 
                            />
                          ) : (
                            order.order_id
                          )}
                        </h6>
                        <small className="text-muted">
                          {t('booking_id')}: 
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => navigate(`/admin/transports/details/${order.transport?.id}`)}
                          >
                            {order.transport?.booking_id}
                          </Button>
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h6 className="mb-0">
                          {order.driver_role === 'Driver' && order.customer_id ? (
                            <Button
                              variant="link"
                              className="p-0 ms-1 text-decoration-none"
                              onClick={() => navigate(`/admin/drivers/details/${order.customer_id}`)}
                            >
                              {order.driver_name || t('not_assigned')}
                            </Button>
                          ) : order.driver_role === 'Driver Company' && order.customer_id ? (
                            <Button
                              variant="link"
                              className="p-0 ms-1 text-decoration-none"
                              onClick={() => navigate(`/admin/drivers-company/details/${order.customer_id}`)}
                            >
                              {order.driver_name || t('not_assigned')}
                            </Button>
                          ) : (
                            <span className="ms-1">{searchTerm ? (
                            <span 
                              dangerouslySetInnerHTML={{
                                  __html: order.driver_name?.replace(
                                    new RegExp(searchTerm, 'gi'), 
                                    match => `<mark class="bg-warning">${match}</mark>`
                                  )
                                }} 
                              />
                              ) : (
                                order.driver_name
                              )}
                            </span>
                          )}
                      </h6>
                      <small className="text-muted">{t(order.driver_role)}</small>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex flex-column align-items-end gap-1">
                      {getStatusBadge(order.status)}
                      {(order.transport?.invoice_status === 'canceled' || order.transport?.invoice_status === 'storniert') && (
                        <Badge bg="warning" text="dark">
                          {t("storniert")}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1">
                      <Badge bg="light" text="dark">
                        {order.price} EUR
                      </Badge>
                    </div>
                  </Col>
                </Row>

                {/* Route Information */}
                <Row className="mb-3">
                  <Col md={5}>
                    <div className="route-info">
                      <div className="d-flex align-items-center mb-2">
                        <FaMapMarkerAlt className="text-success me-2" />
                        <small><strong>{t('from')}:</strong> {order.transport?.booking_from}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaMapMarkerAlt className="text-danger me-2" />
                        <small><strong>{t('to')}:</strong> {order.transport?.booking_to}</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="route-stats">
                      <div className="d-flex align-items-center mb-1">
                        <FaRoad className="text-info me-2" />
                        <small>{order.transport?.distance}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaWeightHanging className="text-warning me-2" />
                        <small>{formatNumber(order.transport?.total_weight)} kg</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="vehicle-info text-end">
                      <div className="d-flex align-items-center justify-content-end mb-1">
                        <FaTruck className="text-secondary me-2" />
                        <small>{order.vehicle_type}</small>
                      </div>
                      <small className="text-muted">{order.vehicle_number}</small>
                      <br />
                      <small className="text-muted">{order.phone_number}</small>
                    </div>
                  </Col>
                </Row>

                {/* Progress and Dates */}
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="progress-bar-container mt-2">
                      <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                        <strong>{t("progress")}:</strong>
                        <span className="progress-label">{getProgressPercentage(order)}%</span>
                      </div>
                      
                      <div className="progress-bar-outer">
                        <div
                          className="progress-bar-inner"
                          style={{
                            width: `${getProgressPercentage(order)}%`,
                            background: getProgressColor(getProgressPercentage(order)),
                          }}
                        ></div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="dates-info text-end">
                      {order.assign_date && (
                        <div className="d-flex justify-content-end align-items-center mb-1">
                          <FaCalendarAlt className="text-muted me-2" />
                          <small>
                            <strong>{t('booking.assigned_on')}:</strong> {order.assign_date}
                          </small>
                        </div>
                      )}
                      {order.received_date && (
                        <div className="d-flex justify-content-end align-items-center mb-1">
                          <FaCalendarAlt className="text-primary me-2" />
                          <small>
                            <strong>{t('booking.received_on')}:</strong> {order.received_date}
                          </small>
                        </div>
                      )}
                      {order.completed_date && (
                        <div className="d-flex justify-content-end align-items-center">
                          <FaCalendarAlt className="text-success me-2" />
                          <small>
                            <strong>{t('booking.completed_on')}:</strong> {order.completed_date}
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <Row className="mt-3 pt-3 border-top">
                  <Col>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleViewDetails(order.id)}
                      >
                        <FaInfoCircle /> {t("details")}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(order.id)}
                      >
                        <FaTrashAlt /> {t("delete")}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))
        )}
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchTransportOrders}
          disabled={loading}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_delete_transport')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
            {loading ? t('deleting') : t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>

      <style>{`
        .booking-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
        }
        
        .booking-highlight .order-header,
        .booking-highlight .order-body,
        .booking-highlight .order-footer {
          background-color: transparent;
        }
        
        .booking-highlight .order-id strong {
          color: #155724;
          font-size: 1.1em;
        }
      `}</style>
    </>
  );
};

export default TransportOrder;